import _ from "lodash"
import { EventConfig, PathwayFilter, DestinationFilter, EventFilter } from "./CohortPathway"
import { findDestinationFilter } from "../reducers"
import { htmlForType as decorateType, htmlForDescription as decorateDescription, htmlForFilter as decorateFilter, ageDescription, arrayToString, arrayToStringComplex } from "../../CohortBuilder/CohortUtil"
import { isNotBlank } from "../../../reducers/util"

export interface EventMetric {
    eventNo: number;
    totalPatients: number;
    lostPatients: number;
    lostPatientsPercent: number;
    metrics: (EmptyDestinationMetric | DestinationMetric)[];
}

export interface EmptyDestinationMetric {
    eventNo: number;
    dataset: number;
    displayOrder: number;
}

export const hasData = (i: (EmptyDestinationMetric | DestinationMetric)): boolean => {
    // @ts-ignore
    return !_.isNil(i.patientCount)
}

export interface DestinationMetric extends EmptyDestinationMetric {
    patientCount: number;
    patientCountPercent: number;
    avgEventCost: number;
    avgPathwayCost: number;
    avgEventDays?: number;
    avgBeddays?: number;
    avgPathwayBeddays?: number;
    medianEventDays?: number;
    deathCount?: number;
    firstAppointmentCount?: number;
    followupAppointmentCount?: number;
    firstAppointmentPercent?: number;
    followupAppointmentPercent?: number;
}

export interface DestinationMetricWithConfig {
    metric: (EmptyDestinationMetric | DestinationMetric);
    config: EventConfig;
}

export const hasDestinationFilter = (i: DestinationMetricWithConfig): boolean => {
    const destinationFilter = findDestinationFilter(i.config, i.metric.dataset)
    return !(_.isNil(destinationFilter) || filterIsEmpty(destinationFilter!.filter))
}

export const filterIsEmpty = (filter: PathwayFilter) => {
    return (_.isNil(filter.primaryDiagnosis) || filter.primaryDiagnosis.length === 0) &&
        (_.isNil(filter.secondaryDiagnosis) || filter.secondaryDiagnosis.length === 0) &&
        (_.isNil(filter.trusts) || filter.trusts.length === 0) &&
        (_.isNil(filter.procedures) || filter.procedures.length === 0) &&
        (_.isNil(filter.hrgs) || filter.hrgs.length === 0) &&
        (_.isNil(filter.treatments) || filter.treatments.length === 0) &&
        (_.isNil(filter.subICBLocations) || filter.subICBLocations.length === 0) &&
        _.isNil(filter.fromAge) &&
        _.isNil(filter.toAge) &&
        (_.isNil(filter.gender) || filter.gender.length === 0)
}

export const isExcludedFromPathway = (i: DestinationMetricWithConfig): boolean => {
    return !i.config.datasets.includes(i.metric.dataset)
}

export const isEventFilter = (filter: (EventFilter | DestinationFilter)) => {
    return "datasets" in filter
}

export const isDestinationFilter = (filter: (EventFilter | DestinationFilter)) => {
    return !isEventFilter(filter)
}

export const eventFilterDescription = (eventFilter: EventFilter) => {
    return filterDescription(eventFilter.filter)
}

export const destinationFilterDescription = (destinationFilter: DestinationFilter) => {
    return filterDescription(destinationFilter.filter)
}

export const filterDescription = (filter: (PathwayFilter)) => {
    const ageText = ageDescription(filter.fromAge, filter.toAge)

    const startText = [
        arrayToString(decorateType('Gender'), decorateDescription, filter.gender),
        isNotBlank(ageText) ? decorateType('Age') + decorateDescription(ageText) : ''
    ].filter(i => i.length > 0).join(', ')

    const diagType =  decorateDescription(filter.diagOr ? decorateType("Pri OR Sec Diagnosis") : decorateType("Pri AND Sec Diagnosis"))

    const hasPrimaryAndSecondaryDiag = !_.isNil(filter.primaryDiagnosis) && filter.primaryDiagnosis.length > 0 && !_.isNil(filter.secondaryDiagnosis) && filter.secondaryDiagnosis.length > 0

    const description = [
        startText,
        hasPrimaryAndSecondaryDiag ? diagType : "",
        arrayToStringComplex(decorateType('Primary Diagnosis'), decorateDescription, filter.primaryDiagnosis),
        arrayToStringComplex(decorateType('Secondary Diagnosis'), decorateDescription, filter.secondaryDiagnosis),
        arrayToStringComplex(decorateType('Treatments'), decorateDescription, filter.treatments),
        arrayToStringComplex(decorateType('Provider Trusts'), decorateDescription, filter.trusts),
        arrayToStringComplex(decorateType('Sub ICB Locations'), decorateDescription, filter.subICBLocations),
        arrayToStringComplex(decorateType('Trusts'), decorateDescription, filter.trusts),
        arrayToStringComplex(decorateType('Procedures'), decorateDescription, filter.procedures),
    ].filter(i => i.length > 0).map(i => decorateFilter(i)).join('')

    return description
}